import type { AxiosInstance } from 'axios';

import { ScanVaultService } from 'api/services/services';

/** Ordered, cumulative filters accepted by the prioritization flow endpoint. */
export enum PrioritizationFlowFilter {
  KnownExploited = 'known_exploited',
  Epss = 'epss',
  Severity = 'severity',
  Patchable = 'patchable',
}

export type PrioritizationFlowMinSeverity = 'critical' | 'high';

export interface PrioritizationFlowSeverityCounts {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  unknown: number;
}

export interface PrioritizationFlowStage {
  /** `all` for the baseline stage, otherwise the filter applied last. */
  key: 'all' | PrioritizationFlowFilter;
  packages: PrioritizationFlowSeverityCounts;
  cves: PrioritizationFlowSeverityCounts;
  hosts: PrioritizationFlowSeverityCounts;
}

export interface PrioritizationFlow {
  filters: PrioritizationFlowFilter[];
  epss_threshold: number;
  min_severity: PrioritizationFlowMinSeverity;
  epss_score_date?: string;
  epss_model_version?: string;
  stages: PrioritizationFlowStage[];
}

export interface PrioritizationFlowParams {
  environmentId?: string;
  accountId?: string | string[];
  resourceTypes?: string[];
  filters: PrioritizationFlowFilter[];
  epssThreshold?: number;
  minSeverity?: PrioritizationFlowMinSeverity;
}

const FLOW_PATH = '/v1/scan-vault/vulnerability/prioritization/flow';

type FlowRequest = {
  environmentId?: string[];
  accountUuid?: string[];
  resourceType?: string[];
  filters?: Set<string>;
  epssGte?: number;
  minSeverity?: string;
};

// The generated client gains getVulnerabilityPrioritizationFlow once
// @ariksa/scan-vault is republished from the updated OpenAPI contract. Until
// then the request goes through the same Axios instance, base path and bearer
// token the generated client uses.
type FlowClient = {
  getVulnerabilityPrioritizationFlow?: (
    request: FlowRequest,
  ) => Promise<{ data?: unknown }>;
  axios: AxiosInstance;
  basePath: string;
  configuration?: {
    accessToken?: string | (() => string | Promise<string>);
  };
};

function toFlowRequest(input: PrioritizationFlowParams): FlowRequest {
  const accounts = input.accountId
    ? Array.isArray(input.accountId)
      ? input.accountId
      : [input.accountId]
    : [];
  return {
    environmentId: input.environmentId ? [input.environmentId] : undefined,
    accountUuid: accounts.length ? accounts : undefined,
    resourceType: input.resourceTypes?.length ? input.resourceTypes : undefined,
    filters: input.filters.length ? new Set(input.filters) : undefined,
    epssGte: input.epssThreshold,
    minSeverity: input.minSeverity,
  };
}

/** Repeated keys (`filters=a&filters=b`) keep the filter order intact. */
export function serializeFlowQuery(request: FlowRequest) {
  const query = new URLSearchParams();
  const append = (key: string, values?: Iterable<string>) => {
    for (const value of values ?? []) query.append(key, value);
  };
  append('environment_id', request.environmentId);
  append('account_uuid', request.accountUuid);
  append('resource_type', request.resourceType);
  append('filters', request.filters);
  if (request.epssGte !== undefined)
    query.set('epss_gte', String(request.epssGte));
  if (request.minSeverity) query.set('min_severity', request.minSeverity);
  return query.toString();
}

export async function getVulnerabilityPrioritizationFlow(
  input: PrioritizationFlowParams,
): Promise<PrioritizationFlow> {
  const client = (ScanVaultService.Vulnerability as unknown) as FlowClient;
  const request = toFlowRequest(input);
  let data: unknown;
  if (typeof client.getVulnerabilityPrioritizationFlow === 'function') {
    data = (await client.getVulnerabilityPrioritizationFlow(request))?.data;
  } else {
    const token = client.configuration?.accessToken;
    const bearer = typeof token === 'function' ? await token() : token;
    const query = serializeFlowQuery(request);
    const response = await client.axios.get(
      `${client.basePath}${FLOW_PATH}${query ? `?${query}` : ''}`,
      { headers: bearer ? { Authorization: `Bearer ${bearer}` } : undefined },
    );
    data = response?.data;
  }
  if (!data || typeof data !== 'object' || !Array.isArray((data as any).stages))
    throw new Error('Vulnerability prioritization is temporarily unavailable');
  return data as PrioritizationFlow;
}
